import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, X } from "lucide-react";

/**
 * Tiny labelled <Input> used across admin forms. Keeps zinc-950 styling.
 */
export function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <Label className="text-xs text-zinc-400">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-zinc-950 border-zinc-800 mt-1"
        data-testid={testId}
      />
    </div>
  );
}

/**
 * Read-only key/value tile used in the contract review step.
 */
export function Info({ label, value, mono }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-3">
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className={`text-zinc-100 ${mono ? "font-mono" : ""} text-sm mt-0.5`}>{value || "—"}</p>
    </div>
  );
}

/**
 * Driver-licence / ID photo slot for the manual-contract wizard.
 * Uses `capture="environment"` so mobile devices open the rear camera
 * directly. Hidden <input type="file"> + clickable styled <label>.
 *
 * Props:
 *  - side      "front" | "back" (used in test ids)
 *  - label     localized label shown in the empty slot ("Fronte" / "Front")
 *  - photo     { url, id } | null
 *  - uploading boolean
 *  - onPick(file)
 *  - onClear()
 *  - isEN      boolean
 */
export function LicensePhotoSlot({ side, label, photo, uploading, onPick, onClear, isEN }) {
  const inputId = `license-${side}-input`;
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  if (photo?.url) {
    const fullUrl = photo.url.startsWith("http") ? photo.url : `${BACKEND_URL}${photo.url}`;
    return (
      <div
        className="relative rounded-lg overflow-hidden border border-emerald-500/30 bg-zinc-950"
        data-testid={`license-${side}-preview`}
      >
        <img src={fullUrl} alt={label} className="w-full h-32 object-cover" />
        <div className="absolute top-1 left-1 bg-black/60 text-[10px] uppercase tracking-widest text-emerald-300 px-2 py-0.5 rounded">
          {label}
        </div>
        <button
          type="button"
          onClick={onClear}
          aria-label="Remove"
          className="absolute top-1 right-1 bg-black/60 hover:bg-red-600/80 text-white rounded-full p-1 transition"
          data-testid={`license-${side}-clear`}
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }
  return (
    <label
      htmlFor={inputId}
      className={`flex flex-col items-center justify-center gap-1 h-32 rounded-lg border-2 border-dashed cursor-pointer transition
                  ${uploading
                    ? "border-[#FFD700] bg-[#FFD700]/10 animate-pulse"
                    : "border-zinc-700 hover:border-[#FFD700] bg-zinc-950/50"}`}
      data-testid={`license-${side}-slot`}
    >
      <Camera className="w-6 h-6 text-zinc-400" />
      <span className="text-xs text-zinc-400">
        {uploading
          ? (isEN ? "Uploading…" : "Caricamento…")
          : (isEN ? `Capture ${label.toLowerCase()}` : `Scatta ${label.toLowerCase()}`)}
      </span>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        disabled={uploading}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
        data-testid={`license-${side}-input`}
      />
    </label>
  );
}
